import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import {
  Tabs as TabsRoot,
  TabsContent,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
} from '../shadcn/tabs';

/**
 * Nava Tabs — matches the Figma Tabs spec.
 * Flat, single-level: pass `items`. Each item is a trigger plus an optional
 * `content` panel; omit `content` for route-driven tabs (wire value/onValueChange
 * to the URL and render your own <Outlet/> below). Per-item `render` is the
 * escape hatch for a bespoke trigger. Axes: variant (boxed / bordered / lifted)
 * × size (sm / md / lg).
 */
const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      boxed: 'gap-0 overflow-hidden rounded-md border border-border-subtle bg-surface p-space-4',
      bordered: 'w-fit gap-space-4 overflow-visible bg-transparent px-space-4 pt-space-4',
      lifted: 'w-fit gap-0 overflow-visible bg-transparent px-space-4 pt-space-4',
    },
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    variant: 'boxed',
    size: 'md',
  },
});

const tabsTriggerVariants = cva(
  cn(
    'relative inline-flex cursor-pointer items-center justify-center gap-space-4 font-medium text-tertiary',
    'transition-colors focus-ring',
    'hover:text-foreground',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg:not([data-tab-corner])]:shrink-0 [&_svg:not([data-tab-corner])]:size-icon-md',
  ),
  {
    variants: {
      variant: {
        boxed: cn(
          'rounded-md border border-transparent',
          'data-[state=active]:border-border-subtle data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        ),
        bordered: cn(
          'rounded-none border-0 border-b-2 border-transparent',
          'data-[state=active]:border-border-inverse data-[state=active]:bg-background data-[state=active]:text-foreground',
        ),
        lifted: cn(
          'rounded-none border-0 border-b border-border',
          'data-[state=active]:z-[1] data-[state=active]:rounded-t-md',
          'data-[state=active]:border data-[state=active]:border-border data-[state=active]:border-b-0',
          'data-[state=active]:bg-background data-[state=active]:text-foreground-brand',
        ),
      },
      size: {
        sm: 'h-space-24 type-text-sm px-space-6 py-space-2',
        md: 'h-[1.75rem] type-text-sm px-space-8 py-space-4', // 28px
        lg: 'h-space-32 type-text-md px-[0.625rem] py-space-6', // 10px
      },
    },
    compoundVariants: [
      {
        variant: 'bordered',
        size: 'sm',
        className: 'h-[1.625rem] px-space-6 pb-space-8 pt-space-4', // 26px
      },
      {
        variant: 'bordered',
        size: 'md',
        className: 'h-[1.875rem] px-space-8 pb-space-8 pt-space-4', // 30px
      },
      {
        variant: 'bordered',
        size: 'lg',
        // 34px / 10px — no space-34 or space-10 tokens
        className: 'h-[2.125rem] px-[0.625rem] pb-space-8 pt-space-4 type-text-md',
      },
      {
        variant: 'lifted',
        size: 'sm',
        className: 'h-space-24 px-space-6 py-space-2',
      },
      {
        variant: 'lifted',
        size: 'md',
        className: 'h-[1.75rem] px-space-8 py-space-4', // 28px
      },
      {
        variant: 'lifted',
        size: 'lg',
        className: 'h-space-32 px-[0.625rem] py-space-6', // 10px
      },
    ],
    defaultVariants: {
      variant: 'boxed',
      size: 'md',
    },
  },
);

export type TabsVariant = NonNullable<VariantProps<typeof tabsListVariants>['variant']>;
export type TabsSize = NonNullable<VariantProps<typeof tabsListVariants>['size']>;

/**
 * Concave bottom corners for lifted active tabs.
 * Quarter-circle arcs (r=8 fill cut, r=9 outer stroke) in a 9×9 box.
 * Vector-sharp when zoomed; token fills — not a Figma asset file.
 */
function LiftedCorner(props: { side: 'left' | 'right' }) {
  let positionClass = 'left-[-9px]';
  let transform: string | undefined;

  if (props.side === 'right') {
    positionClass = 'right-[-9px]';
    transform = 'scaleX(-1)';
  }

  return (
    <svg
      aria-hidden="true"
      data-tab-corner=""
      width="9"
      height="9"
      viewBox="0 0 9 9"
      shapeRendering="geometricPrecision"
      className={cn(
        'pointer-events-none absolute bottom-0 hidden size-[9px] group-data-[state=active]:block',
        positionClass,
      )}
      style={transform ? { transform } : undefined}
    >
      <path d="M0 8A8 8 0 0 1 8 0H9V9H0Z" fill="var(--color-background)" />
      <path d="M9 0A9 9 0 0 1 0 9V8A8 8 0 0 0 8 0Z" fill="var(--color-border)" />
    </svg>
  );
}

function LiftedCorners(props: { variant: TabsVariant }) {
  if (props.variant !== 'lifted') {
    return null;
  }

  return (
    <>
      <LiftedCorner side="left" />
      <LiftedCorner side="right" />
    </>
  );
}

/** One tab: a trigger (label + optional icon, or a custom `render`) and an
 *  optional `content` panel. Omit `content` for route-driven tabs. */
export type TabItem = {
  value: string;
  label?: React.ReactNode;
  content?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Escape hatch — render this as the trigger, ignore label/icon. */
  render?: React.ReactNode;
};

export type TabsProps = React.ComponentProps<typeof TabsRoot> & {
  items: TabItem[];
  variant?: TabsVariant;
  size?: TabsSize;
  /** Forwarded to the trigger list. */
  listClassName?: string;
};

function TriggerContent({ item }: { item: TabItem }) {
  if (item.render !== undefined) {
    return <>{item.render}</>;
  }
  return (
    <>
      {item.icon}
      {item.label}
    </>
  );
}

/** Figma Tabs — flat trail of triggers driven by `items`, each with an optional panel. */
export function Tabs({
  items,
  variant = 'boxed',
  size = 'md',
  listClassName,
  ...props
}: TabsProps) {
  const panels = items.filter((item) => item.content !== undefined);

  return (
    <TabsRoot {...props}>
      <ShadcnTabsList
        className={cn(tabsListVariants({ variant, size }), listClassName)}
        data-variant={variant}
        data-size={size}
      >
        {items.map((item) => (
          <ShadcnTabsTrigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={tabsTriggerVariants({ variant, size })}
            data-variant={variant}
            data-size={size}
          >
            <TriggerContent item={item} />
            <LiftedCorners variant={variant} />
          </ShadcnTabsTrigger>
        ))}
      </ShadcnTabsList>
      {panels.map((item) => (
        <TabsContent key={item.value} value={item.value}>
          {item.content}
        </TabsContent>
      ))}
    </TabsRoot>
  );
}

export { tabsListVariants, tabsTriggerVariants };
